import Color from 'color'

export enum Mode {
  LIGHT = 'light',
  DARK = 'dark',
}
export interface IVariant {
  mode: Mode
  background: string
  interactiveColor: string
  interactiveHover: string
  primaryBackgroundColor: string
  secondaryBackgroundColor: string
  primaryTextColor: string
  secondaryTextColor: string
  primaryHeadingColor: string
  secondaryHeadingColor: string
  primaryBorder: string
  secondaryBorder: string
  tertiaryBackgroundColor: string
}

const defaultVariant: IVariant = {
  mode: Mode.DARK,
  background: '#000000', // BLACK
  interactiveColor: '#E7DCB7', // CREAM
  interactiveHover: '#ED8C00', // TANGERINE
  primaryBackgroundColor: '#101921', // ONYX
  secondaryBackgroundColor: '#141413', // GRAY
  primaryTextColor: '#ffffff', // WHITE
  secondaryTextColor: '#101921', // ONYX
  primaryHeadingColor: '#ED8C00', // TANGERINE
  secondaryHeadingColor: '#E7DCB7', // CREAM
  primaryBorder: '#ED8C00', // TANGERINE
  secondaryBorder: '#E7DCB7', // CREAM
  tertiaryBackgroundColor: '#080808', // ALMOST BLACK
}

export const generateTheme = (variant: IVariant) => {
  const {
    background,
    interactiveColor,
    interactiveHover,
    secondaryTextColor,
    primaryTextColor,
    primaryBackgroundColor,
    secondaryBackgroundColor,
    primaryHeadingColor,
    secondaryHeadingColor,
    primaryBorder,
    secondaryBorder,
    tertiaryBackgroundColor,
  } = variant
  return {
    mode: variant.mode,
    color: {
      background: Color(background),
      interactive: Color(interactiveColor),
      interactiveSecondary: Color(interactiveColor).alpha(0.87),
      interactiveHover: Color(interactiveHover),
      interactiveDisabled: Color(interactiveColor).alpha(0.3),
      listHover: Color(interactiveColor).alpha(0.2),
      primaryBackground: Color(primaryBackgroundColor),
      secondaryBackground: Color(secondaryBackgroundColor),
      primaryText: Color(primaryTextColor),
      secondaryText: Color(secondaryTextColor),
      semiOpaquePrimaryText: Color(primaryTextColor).alpha(0.85),
      primaryHeadingText: Color(primaryHeadingColor),
      secondaryHeadingText: Color(secondaryHeadingColor),
      primaryBorder: Color(primaryBorder),
      secondaryBorder: Color(secondaryBorder),
      tertiaryBorder: Color(secondaryBorder).alpha(0.6),
      panelBackground: Color(primaryBackgroundColor).alpha(0.9),
      overlayBackground: Color(primaryBackgroundColor).alpha(0.95),
      overlayBackgroundSecondary: Color(secondaryBackgroundColor).alpha(0.95),
      loaderBackground: Color(interactiveColor).alpha(0.3),
      smallPlayer: Color(tertiaryBackgroundColor),
      disabled: Color('#e6e6e6'),
      error: Color('#ee7055'),
    },
    font: {
      weight: {
        light: 300,
        base: 400,
        medium: 500,
        bold: 600,
      },
      family: {
        base: '"Source Sans Pro", supria-sans, sans-serif',
        extended: 'Helvetica-Neue-Bold-Extended, supria-sans, sans-serif',
      },
    },
  }
}
export const MAX_WIDTH = '1280px'

export const defaultTheme = generateTheme(defaultVariant)

export type ThemeInterface = ReturnType<typeof generateTheme>
export type ThemeColor = keyof ThemeInterface['color']
export type ThemeFontFamily = keyof ThemeInterface['font']['family']
export type ThemeFontWeight = keyof ThemeInterface['font']['weight']

export type ThemeInterfaceKeys =
  | `color.${ThemeColor}`
  | `font.weight.${keyof ThemeInterface['font']['weight']}`
  | `font.family.${keyof ThemeInterface['font']['family']}`

declare module 'styled-components' {
  export interface DefaultTheme extends ThemeInterface {} // extends the global DefaultTheme with our ThemeType.
}
